// Catalog.ID Client — `create-chat-address` consent modal
// ============================================================================
// WHY THIS MODE HAS A DIALOG WHEN `issue-cert` DOES NOT
//
// `chat-cert-handlers.ts` argues (correctly) that a per-request prompt on cert
// issuance would fire on every device every 60 days and train users to click
// through the one prompt that matters. None of that applies here:
//
//   1. This is the CONSENT PRIMITIVE ITSELF. contact-flow.md §8: "a contact is
//      a mutual `chat_address` share — sharing it *is* consenting to be
//      messaged, and revoking the share is the only access cut-off there is."
//      A consent record written without asking is not a consent record.
//   2. It MUTATES durable account state — a new claim under this account, plus
//      a `claimkey` row handing a named stranger the key to it. `issue-cert`
//      reads one bag entry and signs; nothing persists.
//   3. It is once per contact, not once per renewal cycle. There is no
//      click-through treadmill to build.
//
// WHAT THE DIALOG MUST NAME, and why each line is here
//
//   * the PEER — the whole decision is "this account, or not this account".
//     A dialog that says "share your chat address?" without saying with whom
//     is not a decision, it is a confirmation reflex.
//   * the SIGNING ACCOUNT — the same C7 finding the ceremony and key-publication
//     modals record: an approval binds to whoever is logged in, and a share
//     approved under the wrong identity hands out the wrong reachability.
//   * the RELAY OPERATOR and the chat_encrypt fingerprint — this is *what* is
//     being disclosed. Reachability, not content.
//   * that revoking is the only cut-off (§8), so the user knows what they can
//     undo and what they cannot.
//
// No policy slot ('session' / 'always'): a standing "always share my address"
// grant would let any inbound handshake harvest reachability, which is the
// enumerable-directory outcome plan §4 exists to prevent.

import type { Shell } from '@catalog-shell';
import { createFieldGroup, createFormRow, createElement } from '@catalog-shell';

export type ChatAddressConsentDecision = 'approve' | 'decline';

export interface ChatAddressConsentInput {
    /** The logged-in account whose reachability is being shared. */
    owningCid: string;
    /** The account the claim is shared with — the decision. */
    peerCid: string;
    /** Hub-attested requester (pinned to `chat` upstream; rendered, not trusted). */
    requesterId: string;
    /** Relay operator CODE from the claim value — never a URL (plan §4). */
    relayOperatorId: string;
    /** Short fingerprint of the `chat_encrypt` key inside the value. */
    chatEncryptFingerprint: string;
}

export function showChatAddressConsent(
    shell: Shell,
    input: ChatAddressConsentInput,
): Promise<ChatAddressConsentDecision> {
    return new Promise<ChatAddressConsentDecision>((resolve) => {
        let resolved = false;
        const resolveOnce = (d: ChatAddressConsentDecision): void => {
            if (resolved) return;
            resolved = true;
            handle.close();
            resolve(d);
        };

        const container = createElement('div', { className: 'cs-entity-view cs-modal-body' });

        // The peer is the decision, so it is in the title. `textContent` (never
        // innerHTML) throughout: a CID arrives over a network and is display
        // text, never markup.
        container.appendChild(createElement('h2', {
            className: 'cs-modal-title',
            textContent: `Share your chat address with ${input.peerCid}?`,
        }));

        const accountGroup = createFieldGroup({ title: 'Sharing as' });

        const accountRow = createFormRow({ label: 'Catalog.ID account' });
        accountRow.content.appendChild(createElement('code', {
            className: 'cs-field-view-text',
            textContent: input.owningCid,
        }));
        accountGroup.body.appendChild(accountRow.element);

        const requesterRow = createFormRow({ label: 'Requested by' });
        requesterRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: input.requesterId,
        }));
        accountGroup.body.appendChild(requesterRow.element);
        container.appendChild(accountGroup.element);

        container.appendChild(createElement('div', {
            className: 'cs-inline-warn',
            textContent:
                'Sharing your chat address is how you consent to be messaged. '
                + `${input.peerCid} will be able to reach you until you revoke the share — `
                + 'revoking it is the only way to take it back.',
        }));

        const shareGroup = createFieldGroup({ title: 'What is disclosed' });

        const peerRow = createFormRow({ label: 'Shared with' });
        peerRow.content.appendChild(createElement('code', { textContent: input.peerCid }));
        shareGroup.body.appendChild(peerRow.element);

        const operatorRow = createFormRow({ label: 'Relay operator' });
        operatorRow.content.appendChild(createElement('code', {
            textContent: input.relayOperatorId,
        }));
        shareGroup.body.appendChild(operatorRow.element);

        const keyRow = createFormRow({ label: 'Chat encryption key' });
        keyRow.content.appendChild(createElement('code', {
            textContent: shortFp(input.chatEncryptFingerprint),
            attributes: { title: input.chatEncryptFingerprint },
        }));
        shareGroup.body.appendChild(keyRow.element);

        // Deliberately not "a NEW claim": whether an existing `chat_address`
        // claim is reused can only be known after the keybag is unlocked and its
        // value decrypted, which happens AFTER this dialog. Promising one or the
        // other here would be a guess shown as a fact.
        const claimRow = createFormRow({ label: 'Claim' });
        claimRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: 'Your chat address claim — created now if you have none yet',
        }));
        shareGroup.body.appendChild(claimRow.element);

        container.appendChild(shareGroup.element);

        const actions = createElement('div', { className: 'cs-form-actions' });
        const btnDecline = createElement('button', {
            className: 'cs-btn cs-btn--no',
            textContent: 'Decline',
        }) as HTMLButtonElement;
        btnDecline.addEventListener('click', () => resolveOnce('decline'));
        actions.appendChild(btnDecline);

        const btnApprove = createElement('button', {
            className: 'cs-btn cs-btn--yes',
            textContent: 'Share',
        }) as HTMLButtonElement;
        btnApprove.addEventListener('click', () => resolveOnce('approve'));
        actions.appendChild(btnApprove);
        container.appendChild(actions);

        const handle = shell.showModal(container, {
            size: 'wide',
            dismissOnOverlayClick: false,
            dismissOnEscape: false,
            // A dismissed dialog is a refusal. Never a silent approval, and
            // never a hang: the chat backend is waiting on this promise.
            onClose: () => resolveOnce('decline'),
        });
    });
}

function shortFp(fp: string): string {
    return fp.length <= 24 ? fp : `${fp.slice(0, 8)}…${fp.slice(-8)}`;
}
